import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { updateUser } from '../redux/slices/authSlice';
import PayPolicy from '../components/PayPolicy/PayPolicy';
import SmartBackButton from '../components/SmartBackButton/SmartBackButton';
import { apiHelper } from '../utils/api';
import { useLanguage } from '../context/LanguageContext';
import { Check, Shield, CheckCircle, Crown, Zap, Sparkles } from 'lucide-react';
import './Payment.scss';

const isConfiguredRazorpayKey = (value) =>
  typeof value === 'string' &&
  value.trim().length > 0 &&
  value.trim() !== 'your-razorpay-key-id';

const Payment = () => {
  const { t } = useLanguage();
  const [searchParams] = useSearchParams();
  const plan = (searchParams.get('plan') || 'silver').toLowerCase();

  const [form, setForm] = useState({ name: '', email: '', phone: '' });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [termsPromptVisible, setTermsPromptVisible] = useState(false);
  const [policyOpen, setPolicyOpen] = useState(false);
  const [planSettings, setPlanSettings] = useState(null);
  const [settingsLoading, setSettingsLoading] = useState(true);
  const [settingsError, setSettingsError] = useState('');
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Fetch admin-configured plan settings
  useEffect(() => {
    const fetchPlanSettings = async () => {
      try {
        setSettingsLoading(true);
        setSettingsError('');
        const response = await apiHelper.get('/plans/settings');
        if (response && response.plans) {
          const planData = response.plans.find(p => p.planId === plan);
          if (planData) {
            setPlanSettings(planData);
          } else {
            setPlanSettings(null);
            setSettingsError('Selected plan pricing is not configured by admin.');
          }
        } else {
          setPlanSettings(null);
          setSettingsError('Plan pricing is unavailable right now.');
        }
      } catch (err) {
        console.error('Failed to fetch plan settings:', err);
        setPlanSettings(null);
        setSettingsError('Failed to load plan pricing from admin settings.');
      } finally {
        setSettingsLoading(false);
      }
    };

    fetchPlanSettings();
  }, [plan]);

  // Price must come from admin settings only.
  const formatPrice = (amount) => `₹ ${amount.toLocaleString('en-IN')}`;
  
  const basePrice365 = Number(planSettings?.basePrice365) || 0;
  const discPrice365 = Number(planSettings?.discPrice365) || basePrice365;
  const finalPrice = Number(planSettings?.finalPrice) || discPrice365;
  const savingsNumber = Math.max(0, basePrice365 - discPrice365);
  const hasConfiguredPrice = finalPrice > 0;

  const displayPrice = hasConfiguredPrice ? formatPrice(finalPrice) : '--';
  const displayOriginalPrice = hasConfiguredPrice && basePrice365 > discPrice365
    ? formatPrice(basePrice365)
    : null;
  const displaySavings = savingsNumber > 0 ? formatPrice(savingsNumber) : null;
  const discountPercentage = planSettings?.discountPercentage || 0;
  const offerLabel = planSettings?.offerLabel || '';
  const isOfferActive = planSettings?.isOfferActive || false;
  const displayFeatures = Array.isArray(planSettings?.features)
    ? planSettings.features.filter((feature) => typeof feature === 'string' && feature.trim().length > 0)
    : [];
  const isPriceReady = !settingsLoading && hasConfiguredPrice;

  const handlePay = async (e) => {
    e.preventDefault();
    if (!agreed) {
      setTermsPromptVisible(true);
      return;
    }
    if (!form.name.trim() || !form.email.trim() || !form.phone.trim()) {
      alert('Please enter name, email and phone number');
      return;
    }

    if (!window.Razorpay) {
      alert('Razorpay SDK failed to load. Please check your internet connection.');
      setLoading(false);
      return;
    }

    if (!isPriceReady) {
      alert(settingsError || 'Plan price is not configured by admin. Please contact support.');
      return;
    }

    setLoading(true);
    try {
      // 1. Create order on backend using admin-configured plan pricing (server validates price)
      const amount = finalPrice;
      const order = await apiHelper.post('/payments/order', {
        plan,
        amount,
        receipt: `receipt_${Date.now()}`
      });

      // Prefer server-provided key to avoid relying on build-time env fallbacks
      const razorpayKey = order.keyId;
      if (!isConfiguredRazorpayKey(razorpayKey)) {
        console.error('Razorpay key not provided by server order response:', order);
        alert('Razorpay is not configured correctly on the server. Please contact support.');
        return;
      }

      // 2. Open Razorpay Checkout
      const options = {
        key: razorpayKey,
        amount: order.amount,
        currency: order.currency,
        name: "MIVENT",
        description: `${plan.toUpperCase()} Plan Subscription`,
        order_id: order.id,
        handler: async (response) => {
          try {
            // 3. Verify payment on backend
            const verifyData = {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              plan,
              amount,
              currency: order.currency || 'INR'
            };
            const result = await apiHelper.post('/payments/verify', verifyData);
            if (result.success) {
              dispatch(updateUser({ plan }));
              setSuccess(true);
              try { window.dispatchEvent(new CustomEvent('closePlan')); } catch (e) {}
            } else {
              alert("Payment verification failed");
            }
          } catch (err) {
            console.error(err);
            alert("Error during payment verification");
          }
        },
        prefill: {
          name: form.name,
          email: form.email,
          contact: form.phone,
          method: 'upi'
        },
        config: {
          display: {
            blocks: {
              upi: {
                name: "Pay via UPI",
                instruments: [
                  {
                    method: "upi",
                    vpa: true,
                    upi_qr: true
                  }
                ]
              }
            },
            sequence: ["block.upi", "block.cards", "block.netbanking", "block.wallet"],
            preferences: {
              show_default_blocks: true
            }
          }
        },
        notes: {
          plan: plan
        },
        theme: {
          color: "#007bff",
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.on('payment.failed', async function (response){
        try {
          await apiHelper.post('/payments/failure', {
            orderId: response?.error?.metadata?.order_id || order.id,
            paymentId: response?.error?.metadata?.payment_id || '',
            plan,
            amount,
            currency: order.currency || 'INR',
            failureCode: response?.error?.code || '',
            failureDescription: response?.error?.description || '',
            failureSource: response?.error?.source || '',
            failureStep: response?.error?.step || '',
            failureReason: response?.error?.reason || ''
          });
        } catch (failureLogErr) {
          console.error('Failed to log payment failure:', failureLogErr);
        }
        alert("Payment Failed: " + response.error.description);
      });
      rzp.open();
    } catch (err) {
      console.error(err);
      const msg = err.response?.data?.message || 'Payment initialization failed. Try again later.';
      alert(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="payment-page">
      <div className="payment-card">
        <div className={`payment-card__left ${plan}`}>
          {/* Discount Badge */}
          {isOfferActive && discountPercentage > 0 && (
            <div className="payment-discount-badge">
              {offerLabel || `${discountPercentage}% OFF`}
            </div>
          )}
          
          <div className={`payment-plan ${plan}`}>
            <h3 className="plan-title">
              {plan === 'gold' ? <Crown size={24} className="plan-icon" /> : <Zap size={24} className="plan-icon" />}
              {t(`payment.${plan}Plan`)}
            </h3>
            
            {/* Price with discount */}
            <div className="price-section">
              {isOfferActive && displayOriginalPrice && (
                <div className="original-price-row">
                  <div className="original-price">{displayOriginalPrice}</div>
                  {displaySavings && <div className="original-savings">{t('payment.save')} {displaySavings}</div>}
                </div>
              )}
              <div className={`price ${isOfferActive ? 'price--discounted' : ''}`}>{displayPrice}</div>
            </div>
            <div className="period">{t('payment.periodText')}</div>

            <ul className="benefits">
              {displayFeatures.map((f) => (
                <li key={f}>
                  <Check size={18} className="check-icon" /> {f}
                </li>
              ))}
            </ul>
          </div>

          <div className="payment-security-note">
            <Shield size={20} className="security-icon" />
            <p>{t('payment.securePowered')}</p>
          </div>

          <div className="payment-cta">
                {/* Smart back: closes any open overlay first, else one page back */}
                <SmartBackButton fallback="/dashboard" label={t('payment.back')} className="btn btn--outline" />
                <button className="btn btn--light" onClick={() => navigate('/dashboard')}>
                  <Sparkles size={16} /> {t('payment.managePlan')}
                </button>
          </div>
        </div>

        <div className="payment-card__right">
          {!success ? (
            <form className="payment-form" onSubmit={handlePay}>
              <h2 className="form-title">{t('payment.orderSummary')}</h2>
              
              <div className="summary-item">
                <span>{t('payment.selectedPlan')}</span>
                <strong>{t(`payment.${plan}`)}</strong>
              </div>
              <div className="summary-item">
                <span>{t('payment.billingCycle')}</span>
                <strong>{t('payment.annual')}</strong>
              </div>

              {settingsError && (
                <div className="summary-item" style={{ color: '#dc3545' }}>
                  <span>{settingsError}</span>
                </div>
              )}

              <hr className="divider" />

              <h3 className="section-subtitle">{t('payment.billingInfo')}</h3>
              <p className="section-desc">{t('payment.billingInfoDesc')}</p>

              <label className="input-group">
                <span>{t('payment.formName')}</span>
                <input 
                  required
                  value={form.name} 
                  onChange={(e) => setForm({ ...form, name: e.target.value })} 
                  placeholder="e.g. Vivek" 
                />
              </label>

              <label className="input-group">
                <span>{t('payment.formEmail')}</span>
                <input 
                  required
                  type="email" 
                  value={form.email} 
                  onChange={(e) => setForm({ ...form, email: e.target.value })} 
                  placeholder="e.g. vivek@example.com" 
                />
              </label>

              <label className="input-group">
                <span>{t('payment.formPhone')}</span>
                <input 
                  required
                  type="tel" 
                  value={form.phone} 
                  onChange={(e) => setForm({ ...form, phone: e.target.value })} 
                  placeholder="e.g. 9876543210" 
                />
              </label>

              <div className="payment-total">
                <div className="total-label">{t('payment.payableAmt')}<br/>{t('payment.payableAmtSub')}</div>
                <div className="total-value">{displayPrice}</div>
              </div>

              <div className="payment-actions-container">
                <label className="terms-toggle">
                  <span className="switch">
                    <input type="checkbox" checked={agreed} onChange={(e) => { setAgreed(e.target.checked); setTermsPromptVisible(false); }} />
                    <span className="slider" />
                  </span>
                  <span className="terms-text">{t('payment.iAgree')} <span className="terms-link" onClick={(e) => { e.stopPropagation(); setPolicyOpen(true); }}>{t('payment.termsAndConds')}</span></span>
                </label>
                {termsPromptVisible && !agreed && (
                  <div className="payment-terms-error" role="alert">
                    Please agree to the Terms &amp; Conditions before continuing.
                  </div>
                )}
                <div className="payment-buttons-stack">
                  <button type="submit" className={`btn btn--primary btn--large w-100${!agreed ? ' payment-btn--terms-required' : ''}`} disabled={loading || !isPriceReady} aria-disabled={!agreed}>
                    {loading ? t('payment.initializing') : <>{t('payment.securelyPay')} {displayPrice}</>}
                  </button>
                </div>
              </div>

              <div className="trust-badges">
                <img src="https://img.shields.io/badge/Razorpay-Verified-blue.svg" alt="Razorpay" />
                <span>{t('payment.pciCompliant')}</span>
              </div>
            </form>
          ) : (
            <div className="payment-success">
              <div className="success-icon"><CheckCircle size={64} className="success-check" /></div>
              <h2>{t('payment.subActive')}</h2>
              <p>{t('payment.planActivated').replace('{plan}', plan)}</p>
              <div className="payment-success__actions">
                <button className="btn btn--primary" onClick={() => navigate('/dashboard')}>{t('payment.goDashboard')}</button>
              </div>
            </div>
          )}
        </div>
      </div>
      <PayPolicy open={policyOpen} onClose={() => setPolicyOpen(false)} />
    </div>
  );
};

export default Payment;
